import { useRef } from 'react';
import styled from 'styled-components';
import { useDeepCompareEffect } from 'react-use';
import { KoobButton, KoobInputStars } from '@koob/ui';
import { useBooking, useSearchParams } from 'hooks';
import { Stack, Dropdown, PopoverMenu } from '@koob/margaret';
import { useTranslation } from 'react-i18next';
import LocationAwareSelect from 'components/LocationAwareSelect';
import {
  KoobLocationSearch,
  SEARCH_DEFAULT_TYPES,
  SEARCH_TYPES,
} from '@koob/search';
import { getKinds } from 'api/node/kinds';
import { useAsync } from 'react-async';

const LOCATION_SEARCH_TYPES = [...SEARCH_DEFAULT_TYPES, SEARCH_TYPES.HOTEL];

const FiltersPopoverMenu = styled(PopoverMenu)`
  overflow: visible !important;
  width: 400px;
  max-height: inherit;
  position: relative;
  /* right-align the panel on the trigger: 100% is the trigger's width */
  left: calc(100% - 400px);
  /* the trigger now sits in normal page flow (not the elevated Header), so
     the popover must stack above the card grid on its own */
  z-index: 20;
`;

const FilterWrapper = styled(Stack).attrs({
  alignY: 'center',
  gutterSize: 1.5,
  paddingTop: 0.75,
  paddingRight: 1,
  paddingLeft: 1,
})``;

const FilterHotels = ({ showLocation = true, showKind = true }) => {
  const { t, i18n } = useTranslation('bookings');
  const filtersDropdownRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const stars = Boolean(searchParams.stars) ? Number(searchParams.stars) : null;
  const { setFilters } = useBooking();
  const { data: kindsData } = useAsync({
    promiseFn: getKinds,
  });

  useDeepCompareEffect(() => {
    if (Boolean(filtersDropdownRef.current)) {
      filtersDropdownRef.current.close();
    }
  }, [{ ...searchParams }]);

  return (
    <Dropdown
      ref={filtersDropdownRef}
      trigger={
        <KoobButton variant="secondary" asChild>
          <span>
            <Stack alignY="center" gutterSize={0.5}>
              <Stack>
                <i className="fa-light fa-bars-filter" />
              </Stack>
              <span>{t('filters')}</span>
            </Stack>
          </span>
        </KoobButton>
      }
    >
      <FiltersPopoverMenu>
        <FilterWrapper style={{ paddingTop: 30 }}>
          <p style={{ width: 150 }}>{t('stars')}</p>
          <Stack size="full" style={{ margin: 0 }}>
            <KoobInputStars
              value={stars}
              onValueChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  stars: value,
                })
              }
            />
          </Stack>
        </FilterWrapper>

        {showKind && (
          <FilterWrapper>
            <p style={{ width: 150 }}>{t('kindOfHotel')}</p>
            <Stack size="full" style={{ margin: 0 }}>
              <LocationAwareSelect
                debounceTime={0}
                name="kindsIds"
                options={kindsData?.data?.map((node) => ({
                  label: node?.displayName,
                  value: node?.id,
                }))}
                renderValue={(value) => value?.label}
                renderOption={(value) => value?.label}
                multiple
                shouldHideSelectedValues
                shouldStoreWholeObject
              />
            </Stack>
          </FilterWrapper>
        )}

        {showLocation && (
          <FilterWrapper>
            <p style={{ width: 150 }}>{t('location_hotel')}</p>
            <Stack size="full" style={{ margin: 0 }}>
              <KoobLocationSearch
                value={searchParams?.searchLocation}
                onValueChange={(value) => {
                  setSearchParams({
                    ...searchParams,
                    searchLocation: value,
                  });
                }}
                types={LOCATION_SEARCH_TYPES}
                locale={i18n.language}
                pluralKinds
              />
            </Stack>
          </FilterWrapper>
        )}

        <Stack
          alignX="flex-end"
          alignY="baseline"
          gutterSize={1}
          paddingTop={1}
          paddingRight={1}
          paddingBottom={1}
        >
          <KoobButton
            variant="secondary"
            type="button"
            onClick={() => {
              setSearchParams();
              setFilters({});
            }}
          >
            {t('resetFilters')}
          </KoobButton>
          <KoobButton
            variant="primary"
            type="button"
            onClick={() => {
              if (Boolean(filtersDropdownRef.current)) {
                filtersDropdownRef.current.close();
              }
            }}
          >
            {t('misc:search')}
          </KoobButton>
        </Stack>
      </FiltersPopoverMenu>
    </Dropdown>
  );
};

export default FilterHotels;
